import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Loader2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';

interface ProposalTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  pricingModel: 'fixed' | 'hourly' | 'hybrid';
  estimatedValue: number;
  timeline: string;
  sections: {
    executiveSummary: string;
    scopeOfWork: string;
    deliverables: string[];
    timeline: string;
    pricing: string;
    assumptions: string[];
    exclusions: string[];
  };
}

interface CreateProposalTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  accessToken: string;
  firmId: string;
  editingTemplate?: ProposalTemplate;
}

export function CreateProposalTemplateDialog({
  open,
  onClose,
  onSuccess,
  accessToken,
  firmId,
  editingTemplate,
}: CreateProposalTemplateDialogProps) {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'implementation',
    pricingModel: 'fixed' as 'fixed' | 'hourly' | 'hybrid',
    estimatedValue: 0,
    timeline: '12 weeks',
    sections: {
      executiveSummary: '',
      scopeOfWork: '',
      deliverables: [''],
      timeline: '',
      pricing: '',
      assumptions: [''],
      exclusions: [''],
    },
  });

  // Populate form when editing
  useEffect(() => {
    if (editingTemplate) {
      setFormData({
        name: editingTemplate.name,
        description: editingTemplate.description,
        category: editingTemplate.category,
        pricingModel: editingTemplate.pricingModel,
        estimatedValue: editingTemplate.estimatedValue,
        timeline: editingTemplate.timeline,
        sections: editingTemplate.sections,
      });
    } else {
      // Reset form when creating new
      setFormData({
        name: '',
        description: '',
        category: 'implementation',
        pricingModel: 'fixed',
        estimatedValue: 0,
        timeline: '12 weeks',
        sections: {
          executiveSummary: '',
          scopeOfWork: '',
          deliverables: [''],
          timeline: '',
          pricing: '',
          assumptions: [''],
          exclusions: [''],
        },
      });
    }
  }, [editingTemplate, open]);

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a template name');
      return;
    }

    setSaving(true);
    try {
      const url = editingTemplate
        ? `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/proposal-templates/${editingTemplate.id}`
        : `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/proposal-templates`;

      const method = editingTemplate ? 'PATCH' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          ...formData,
          // Filter out empty strings from arrays
          sections: {
            ...formData.sections,
            deliverables: formData.sections.deliverables.filter(d => d.trim()),
            assumptions: formData.sections.assumptions.filter(a => a.trim()),
            exclusions: formData.sections.exclusions.filter(e => e.trim()),
          },
        }),
      });

      if (response.ok) {
        toast.success(editingTemplate ? 'Template updated successfully' : 'Template created successfully');
        onSuccess();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to save template');
      }
    } catch (error) {
      console.error('Error saving proposal template:', error);
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const addDeliverable = () => {
    setFormData({
      ...formData,
      sections: {
        ...formData.sections,
        deliverables: [...formData.sections.deliverables, ''],
      },
    });
  };

  const removeDeliverable = (index: number) => {
    setFormData({
      ...formData,
      sections: {
        ...formData.sections,
        deliverables: formData.sections.deliverables.filter((_, i) => i !== index),
      },
    });
  };

  const updateDeliverable = (index: number, value: string) => {
    const newDeliverables = [...formData.sections.deliverables];
    newDeliverables[index] = value;
    setFormData({
      ...formData,
      sections: {
        ...formData.sections,
        deliverables: newDeliverables,
      },
    });
  };

  const addAssumption = () => {
    setFormData({
      ...formData,
      sections: {
        ...formData.sections,
        assumptions: [...formData.sections.assumptions, ''],
      },
    });
  };

  const removeAssumption = (index: number) => {
    setFormData({
      ...formData,
      sections: {
        ...formData.sections,
        assumptions: formData.sections.assumptions.filter((_, i) => i !== index),
      },
    });
  };

  const updateAssumption = (index: number, value: string) => {
    const newAssumptions = [...formData.sections.assumptions];
    newAssumptions[index] = value;
    setFormData({
      ...formData,
      sections: {
        ...formData.sections,
        assumptions: newAssumptions,
      },
    });
  };

  const addExclusion = () => {
    setFormData({
      ...formData,
      sections: {
        ...formData.sections,
        exclusions: [...formData.sections.exclusions, ''],
      },
    });
  };

  const removeExclusion = (index: number) => {
    setFormData({
      ...formData,
      sections: {
        ...formData.sections,
        exclusions: formData.sections.exclusions.filter((_, i) => i !== index),
      },
    });
  };

  const updateExclusion = (index: number, value: string) => {
    const newExclusions = [...formData.sections.exclusions];
    newExclusions[index] = value;
    setFormData({
      ...formData,
      sections: {
        ...formData.sections,
        exclusions: newExclusions,
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingTemplate ? 'Edit Proposal Template' : 'Create Proposal Template'}
          </DialogTitle>
          <DialogDescription>
            Create reusable proposal templates for faster deal creation
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="basic" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="content">Content</TabsTrigger>
            <TabsTrigger value="terms">Terms & Conditions</TabsTrigger>
          </TabsList>

          {/* Basic Info Tab */}
          <TabsContent value="basic" className="space-y-4">
            <div>
              <Label htmlFor="name">Template Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., ServiceNow Discovery & Scoping"
              />
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this proposal template..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="category">Category</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="discovery">Discovery</SelectItem>
                    <SelectItem value="implementation">Implementation</SelectItem>
                    <SelectItem value="support">Support</SelectItem>
                    <SelectItem value="consulting">Consulting</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="pricingModel">Pricing Model</Label>
                <Select value={formData.pricingModel} onValueChange={(value: any) => setFormData({ ...formData, pricingModel: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed">Fixed Price</SelectItem>
                    <SelectItem value="hourly">Hourly Rate</SelectItem>
                    <SelectItem value="hybrid">Hybrid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="estimatedValue">Estimated Value ($)</Label>
                <Input
                  id="estimatedValue"
                  type="number"
                  value={formData.estimatedValue}
                  onChange={(e) => setFormData({ ...formData, estimatedValue: parseFloat(e.target.value) || 0 })}
                  placeholder="75000"
                />
              </div>

              <div>
                <Label htmlFor="timeline">Timeline</Label>
                <Input
                  id="timeline"
                  value={formData.timeline}
                  onChange={(e) => setFormData({ ...formData, timeline: e.target.value })}
                  placeholder="12 weeks"
                />
              </div>
            </div>
          </TabsContent>

          {/* Content Tab */}
          <TabsContent value="content" className="space-y-4">
            <div>
              <Label htmlFor="executiveSummary">Executive Summary</Label>
              <Textarea
                id="executiveSummary"
                value={formData.sections.executiveSummary}
                onChange={(e) => setFormData({
                  ...formData,
                  sections: { ...formData.sections, executiveSummary: e.target.value },
                })}
                placeholder="High-level overview of the proposal..."
                rows={4}
              />
            </div>

            <div>
              <Label htmlFor="scopeOfWork">Scope of Work</Label>
              <Textarea
                id="scopeOfWork"
                value={formData.sections.scopeOfWork}
                onChange={(e) => setFormData({
                  ...formData,
                  sections: { ...formData.sections, scopeOfWork: e.target.value },
                })}
                placeholder="Detailed description of work to be performed..."
                rows={6}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Deliverables</Label>
                <Button type="button" size="sm" variant="outline" onClick={addDeliverable}>
                  <Plus className="size-4 mr-2" />
                  Add Deliverable
                </Button>
              </div>
              <div className="space-y-2">
                {formData.sections.deliverables.map((deliverable, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={deliverable}
                      onChange={(e) => updateDeliverable(index, e.target.value)}
                      placeholder={`Deliverable ${index + 1}`}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => removeDeliverable(index)}
                    >
                      <X className="size-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="sectionTimeline">Timeline Details</Label>
              <Textarea
                id="sectionTimeline"
                value={formData.sections.timeline}
                onChange={(e) => setFormData({
                  ...formData,
                  sections: { ...formData.sections, timeline: e.target.value },
                })}
                placeholder="Phase 1: Discovery (2 weeks)&#10;Phase 2: Implementation (8 weeks)&#10;Phase 3: Testing (2 weeks)"
                rows={4}
              />
            </div>

            <div>
              <Label htmlFor="pricing">Pricing Details</Label>
              <Textarea
                id="pricing"
                value={formData.sections.pricing}
                onChange={(e) => setFormData({
                  ...formData,
                  sections: { ...formData.sections, pricing: e.target.value },
                })}
                placeholder="Breakdown of pricing, payment terms, etc."
                rows={4}
              />
            </div>
          </TabsContent>

          {/* Terms Tab */}
          <TabsContent value="terms" className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Assumptions</Label>
                <Button type="button" size="sm" variant="outline" onClick={addAssumption}>
                  <Plus className="size-4 mr-2" />
                  Add Assumption
                </Button>
              </div>
              <div className="space-y-2">
                {formData.sections.assumptions.map((assumption, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={assumption}
                      onChange={(e) => updateAssumption(index, e.target.value)}
                      placeholder={`Assumption ${index + 1}`}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => removeAssumption(index)}
                    >
                      <X className="size-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Exclusions</Label>
                <Button type="button" size="sm" variant="outline" onClick={addExclusion}>
                  <Plus className="size-4 mr-2" />
                  Add Exclusion
                </Button>
              </div>
              <div className="space-y-2">
                {formData.sections.exclusions.map((exclusion, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={exclusion}
                      onChange={(e) => updateExclusion(index, e.target.value)}
                      placeholder={`Exclusion ${index + 1}`}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => removeExclusion(index)}
                    >
                      <X className="size-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="size-4 mr-2 animate-spin" />}
            {editingTemplate ? 'Update Template' : 'Create Template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
